import { c as _c } from "react/compiler-runtime";
import { Snowflake, Zap, Flame, Mountain, Bike, ShieldCheck, Baby, Star, Trophy, HardHat, Footprints, Sparkles, Wrench, Shield, Bandage, Disc, Cog, CircleDot, ChevronsUpDown, Quote, Wind, type LucideIcon } from 'lucide-react';
type Visual = {
  color: string;
  Icon: LucideIcon;
};

/**
 * One signature colour + icon per pack, rental item, and atelier category.
 * An alpine-leather palette: earthy, mid-dark hues that sit at home on the
 * warm leather and read cleanly on the ivory concho behind each glyph.
 * Keyed by the item `id` used across the rental / workshop content files.
 */
const ALPINE = {
  glacier: '#2f6f95',
  // cold blue
  teal: '#2c7a72',
  pine: '#3f6b4a',
  moss: '#6b7f3a',
  ochre: '#b07d1f',
  // warm gold
  amber: '#c26a1c',
  terracotta: '#b8492e',
  // hot
  clay: '#a85535',
  slate: '#566b7d',
  aubergine: '#6e4a6f',
  wine: '#8a3d5f',
  gold: '#c9a15a' // brand leather gold
} as const;
const VISUALS: Record<string, Visual> = {
  // Winter rental packs — cool→warm as ability rises
  loisir: {
    color: ALPINE.glacier,
    Icon: Snowflake
  },
  sensation: {
    color: ALPINE.ochre,
    Icon: Zap
  },
  performance: {
    color: ALPINE.terracotta,
    Icon: Flame
  },
  rando: {
    color: ALPINE.pine,
    Icon: Mountain
  },
  snowscoot: {
    color: ALPINE.teal,
    Icon: Wind
  },
  insurance: {
    color: ALPINE.slate,
    Icon: ShieldCheck
  },
  'mini-kids': {
    color: ALPINE.moss,
    Icon: Baby
  },
  junior: {
    color: ALPINE.amber,
    Icon: Star
  },
  champion: {
    color: ALPINE.wine,
    Icon: Trophy
  },
  // Winter other rentals
  helmet: {
    color: ALPINE.clay,
    Icon: HardHat
  },
  snowshoes: {
    color: ALPINE.glacier,
    Icon: Footprints
  },
  sleds: {
    color: ALPINE.teal,
    Icon: Sparkles
  },
  // Summer rental packs / other
  'ns-dh-v5': {
    color: ALPINE.terracotta,
    Icon: Bike
  },
  'full-face-helmet': {
    color: ALPINE.clay,
    Icon: HardHat
  },
  'body-armor': {
    color: ALPINE.aubergine,
    Icon: Shield
  },
  'knee-elbow-pads': {
    color: ALPINE.ochre,
    Icon: Bandage
  },
  // Winter atelier categories
  'ski-snow-maintenance': {
    color: ALPINE.glacier,
    Icon: Sparkles
  },
  'ski-snow-repairs': {
    color: ALPINE.ochre,
    Icon: Wrench
  },
  'touring-services': {
    color: ALPINE.pine,
    Icon: Mountain
  },
  // Summer atelier categories
  'bike-maintenance': {
    color: ALPINE.glacier,
    Icon: Wrench
  },
  'suspension-service': {
    color: ALPINE.ochre,
    Icon: ChevronsUpDown
  },
  'brake-service': {
    color: ALPINE.terracotta,
    Icon: Disc
  },
  'drivetrain-service': {
    color: ALPINE.teal,
    Icon: Cog
  },
  'wheel-tire-service': {
    color: ALPINE.aubergine,
    Icon: CircleDot
  },
  'upgrades-custom': {
    color: ALPINE.wine,
    Icon: Sparkles
  },
  // Misc
  testimonial: {
    color: ALPINE.gold,
    Icon: Quote
  }
};
const FALLBACK: Visual = {
  color: '#c9a15a',
  Icon: Sparkles
};
export function visualFor(id: string): Visual {
  return VISUALS[id] ?? FALLBACK;
}

/**
 * Card / section header on the brand leather texture: a colour-coded circled
 * icon + the name, with a thin accent bar in the item's signature colour.
 */
// Shared leather-patch treatment: burnished inset edge + top emboss + bottom
// vignette make the /leather.jpg crop read as a real stamped patch.
const LEATHER = "bg-[url('/leather.jpg')] bg-cover bg-center shadow-[inset_0_0_0_1px_rgba(42,31,16,0.55),inset_0_2px_2px_rgba(255,240,214,0.30),inset_0_-12px_22px_-8px_rgba(0,0,0,0.55)]";
// Neutral dark medallion (concho) behind the colour-coded glyph.
const MEDALLION = 'shrink-0 rounded-full bg-[#f4ead0] box-content shadow-[inset_0_-2px_3px_rgba(74,49,20,0.20),0_1px_2px_rgba(0,0,0,0.30)] ring-1 ring-[#5b4326]/35';
// Engraved black title, lit from below. Tight leading so a two-line name reads
// as one stacked block rather than two loose rows.
const ENGRAVED = 'min-w-0 text-center font-bold uppercase font-[family-name:var(--font-bebas-neue)] leading-[1.05] tracking-wide text-black [text-shadow:0_1px_0_rgba(255,240,214,0.55)]';

/**
 * Card / section header for the rental & workshop pages.
 * - variant="plain" (default): quiet section title, icon on the right — used by
 *   the workshop tables, which stay understated.
 * - variant="leather": the branded leather patch — colour-coded glyph on a
 *   neutral medallion, engraved title, ecru saddle-stitch edge — used by the
 *   rental packs so the colour code lives in the icon, not a loud fill.
 */
export function LeatherHeader(t0) {
  const $ = _c(23);
  const {
    name,
    color: t1,
    Icon,
    size: t2,
    variant: t3
  } = t0;
  const color = t1 === undefined ? "#c9a15a" : t1;
  const size = t2 === undefined ? "lg" : t2;
  const variant = t3 === undefined ? "plain" : t3;
  const compact = size === "sm";
  if (variant === "leather") {
    const t4 = `flex items-center justify-center gap-3 border-b-[3px] border-dashed border-[#efe4c8] ${LEATHER} ${compact ? "min-h-[5.75rem] px-4 py-3" : "min-h-[6.5rem] px-5 py-3"}`;
    const t5 = `${MEDALLION} ${compact ? "h-6 w-6 p-1.5" : "h-9 w-9 p-2"}`;
    let t6;
    if ($[0] !== color) {
      t6 = {
        color
      };
      $[0] = color;
      $[1] = t6;
    } else {
      t6 = $[1];
    }
    let t7;
    if ($[2] !== Icon || $[3] !== t5 || $[4] !== t6) {
      t7 = <Icon className={t5} style={t6} strokeWidth={2.25} />;
      $[2] = Icon;
      $[3] = t5;
      $[4] = t6;
      $[5] = t7;
    } else {
      t7 = $[5];
    }
    const t8 = `${ENGRAVED} ${compact ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl"}`;
    let t9;
    if ($[6] !== name || $[7] !== t8) {
      t9 = <h3 className={t8}>{name}</h3>;
      $[6] = name;
      $[7] = t8;
      $[8] = t9;
    } else {
      t9 = $[8];
    }
    let t10;
    if ($[9] !== t4 || $[10] !== t7 || $[11] !== t9) {
      t10 = <div className={t4}>{t7}{t9}</div>;
      $[9] = t4;
      $[10] = t7;
      $[11] = t9;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    return t10;
  }
  const t4 = `flex items-center gap-3 px-5 sm:px-6 ${compact ? "py-3" : "py-5 sm:py-6"}`;
  const t5 = `shrink-0 text-primary ${compact ? "h-6 w-6" : "h-7 w-7 sm:h-8 sm:w-8 md:h-9 md:w-9"}`;
  let t6;
  if ($[13] !== Icon || $[14] !== t5) {
    t6 = <Icon className={t5} strokeWidth={2} />;
    $[13] = Icon;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = `min-w-0 font-bold uppercase font-[family-name:var(--font-bebas-neue)] leading-tight tracking-normal text-foreground ${compact ? "text-2xl md:text-3xl" : "text-3xl sm:text-4xl"}`;
  let t8;
  if ($[16] !== name || $[17] !== t7) {
    t8 = <h3 className={t7}>{name}</h3>;
    $[16] = name;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t6 || $[21] !== t8) {
    t9 = <div className={t4}>{t6}{t8}</div>;
    $[19] = t4;
    $[20] = t6;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}